import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTIVITIES, ActivityOption } from '../pages/project-executor-planning/activity-constants';
import { AsphaltPerformerPlanningEntry, ProjectExecutorPlanningEntry } from '../services/executor-planning.service';

// Union type for entries that can appear in duplicates
export type DuplicateEntry = ProjectExecutorPlanningEntry | AsphaltPerformerPlanningEntry;

export interface DuplicateEntrySelectDialogData {
    entries: DuplicateEntry[];
    projectName: string;
    date: string; // YYYY-MM-DD
}

export interface DuplicateEntrySelectDialogResult {
    action: 'delete' | 'move_date';
    entry: ProjectExecutorPlanningEntry;
    newDate?: Date;
}

@Component({
    selector: 'app-duplicate-entry-select-dialog',
    templateUrl: './duplicate-entry-select-dialog.component.html',
    styleUrl: './duplicate-entry-select-dialog.component.scss',
    standalone: false,
})
export class DuplicateEntrySelectDialogComponent implements OnInit {
    activities: ActivityOption[] = ACTIVITIES;
    selectedEntry: DuplicateEntry | null = null;
    dateControl = new FormControl<Date | null>(null);
    formattedDate: string;

    constructor(
        private dialogRef: MatDialogRef<DuplicateEntrySelectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DuplicateEntrySelectDialogData
    ) {
        this.formattedDate = new Date(data.date + 'T00:00:00').toLocaleDateString('nl-NL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }

    ngOnInit(): void {
        this.dateControl.setValue(new Date(this.data.date));
    }

    selectEntry(entry: DuplicateEntry): void {
        // Don't allow selecting asphalt entries
        if (this.isAsphaltEntry(entry)) {
            return;
        }
        this.selectedEntry = entry;
    }

    isSelected(entry: DuplicateEntry): boolean {
        if (!this.selectedEntry) {
            return false;
        }

        const isEntryAsphalt = this.isAsphaltEntry(entry);
        const isSelectedAsphalt = this.isAsphaltEntry(this.selectedEntry);

        // Beide zijn asphalt entries - vergelijk op basis van user_id, project en datum
        if (isEntryAsphalt && isSelectedAsphalt) {
            return (
                this.selectedEntry.user_id === entry.user_id &&
                this.selectedEntry.afas_project_id === entry.afas_project_id &&
                this.selectedEntry.date === entry.date
            );
        }

        // Beide zijn reguliere planning entries - vergelijk op basis van ID
        if (!isEntryAsphalt && !isSelectedAsphalt) {
            return (
                (this.selectedEntry as ProjectExecutorPlanningEntry).id === (entry as ProjectExecutorPlanningEntry).id
            );
        }

        // Eén is asphalt en de ander niet - kunnen niet hetzelfde zijn
        return false;
    }

    isAsphaltEntry(entry: DuplicateEntry | null): entry is AsphaltPerformerPlanningEntry {
        return entry !== null && !('id' in entry);
    }

    canDeleteOrMove(entry: DuplicateEntry): boolean {
        return !this.isAsphaltEntry(entry);
    }

    getTimeLabel(entry: DuplicateEntry): string {
        if (this.isAsphaltEntry(entry)) {
            if (entry.start_time && entry.end_time) {
                return `${entry.start_time} - ${entry.end_time}`;
            }

            if (entry.start_time) {
                return `${entry.start_time}`;
            }

            return '';
        }
        const hour = new Date(entry.date).getHours();
        return hour >= 18 ? 'Nacht (20:00)' : 'Dag (08:00)';
    }

    getActivityLabel(entry: DuplicateEntry): string {
        if (this.isAsphaltEntry(entry)) {
            if (entry.entitytype_key === 'cutter') {
                return 'Frezen';
            }
            return 'Asfalt';
        }
        return (
            this.activities.find(a => a.value === (entry as ProjectExecutorPlanningEntry).activity)?.label ??
            (entry as ProjectExecutorPlanningEntry).activity
        );
    }

    getActivityIcon(entry: DuplicateEntry): string {
        if (this.isAsphaltEntry(entry)) {
            return 'build';
        }
        return (
            this.activities.find(a => a.value === (entry as ProjectExecutorPlanningEntry).activity)?.icon ??
            'more_horiz'
        );
    }

    onDeleteEntry(): void {
        if (!this.selectedEntry || this.isAsphaltEntry(this.selectedEntry)) return;
        this.dialogRef.close({
            action: 'delete',
            entry: this.selectedEntry as ProjectExecutorPlanningEntry,
        } as DuplicateEntrySelectDialogResult);
    }

    onMoveDate(): void {
        if (!this.selectedEntry || !this.dateControl.value || this.isAsphaltEntry(this.selectedEntry)) return;
        this.dialogRef.close({
            action: 'move_date',
            entry: this.selectedEntry as ProjectExecutorPlanningEntry,
            newDate: this.dateControl.value,
        } as DuplicateEntrySelectDialogResult);
    }

    onCancel(): void {
        this.dialogRef.close(null);
    }
}
